/* Featured-products strip — behavior port of live's legacy-prefixed custom
 * element (assets/custom.js): tab switching, drag-to-scroll at 2x pointer
 * speed, and edge-aware arrow paging by one card width plus the track gap.
 * Live's element also carried an addVariantToCart method nothing ever called;
 * it is not ported. Add-to-cart is the card's own availability-gated form.
 *
 * Enhancement only: without JS the track still scrolls natively
 * (overflow-x: auto) and every card link and form keeps working.
 */
class FeaturedProducts extends HTMLElement {
  connectedCallback() {
    this.tabTitles = this.querySelectorAll('.featured-products__tabs li');
    this.tracks = this.querySelectorAll('.featured-products__products');
    this.arrowContainers = this.querySelectorAll('.featured-products__buttons');

    this.tracks.forEach((track) => {
      this.initDragScroll(track);
      this.updateArrows(track);
      track.addEventListener('scroll', (event) => this.updateArrows(event.target));
    });

    this.arrowContainers.forEach((container) => {
      const left = container.querySelector('.featured-products__buttons-left');
      const right = container.querySelector('.featured-products__buttons-right');
      const track = this.querySelector(`.featured-products__products[data-id="${container.dataset.id}"]`);
      if (!left || !right || !track) return;
      // Live pages by first-card width + 16 (its gap). The gap is read from
      // the computed style so the two cannot drift apart.
      const step = () => {
        const card = track.querySelector('li');
        if (!card) return 0;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 16;
        return card.offsetWidth + gap;
      };
      left.addEventListener('click', () => this.scrollTrack(track, -step()));
      right.addEventListener('click', () => this.scrollTrack(track, step()));
    });

    this.initTabs();
  }

  disconnectedCallback() {
    this.tabTitles = null;
    this.tracks = null;
    this.arrowContainers = null;
  }

  updateArrows(track) {
    const container = Array.from(this.arrowContainers).find(
      (candidate) => candidate.dataset.id === track.dataset.id
    );
    if (!container) return;
    // Live hides the arrows entirely below 5 cards.
    if (track.childElementCount < 5) {
      container.hidden = true;
      return;
    }
    const left = container.querySelector('.featured-products__buttons-left');
    const right = container.querySelector('.featured-products__buttons-right');
    // Live's thresholds: right disables within 10px of the end, left within 3px
    // of the start.
    if (track.scrollWidth - track.clientWidth - track.scrollLeft < 10) {
      right.setAttribute('disabled', '');
    } else {
      right.removeAttribute('disabled');
    }
    if (track.scrollLeft > 3) {
      left.removeAttribute('disabled');
    } else {
      left.setAttribute('disabled', '');
    }
  }

  initTabs() {
    this.tabTitles.forEach((tab) => {
      tab.addEventListener('click', () => {
        const id = tab.dataset.id;
        this.tabTitles.forEach((candidate) => {
          candidate.setAttribute('data-active', candidate.dataset.id === id);
        });
        this.tracks.forEach((candidate) => {
          candidate.setAttribute('data-active', candidate.dataset.id === id);
          if (candidate.dataset.id === id) this.updateArrows(candidate);
        });
        this.arrowContainers.forEach((candidate) => {
          candidate.setAttribute('data-active', candidate.dataset.id === id);
        });
      });
    });
  }

  scrollTrack(track, distance) {
    track.scrollTo({ left: track.scrollLeft + distance, behavior: 'smooth' });
  }

  initDragScroll(track) {
    let dragging = false;
    let startX;
    let startScrollLeft;
    track.addEventListener('mousedown', (event) => {
      event.preventDefault();
      dragging = true;
      startX = event.pageX - track.offsetLeft;
      startScrollLeft = track.scrollLeft;
      track.classList.add('dragging');
    });
    track.addEventListener('mouseleave', () => {
      dragging = false;
      track.classList.remove('dragging');
    });
    track.addEventListener('mouseup', () => {
      dragging = false;
      track.classList.remove('dragging');
    });
    track.addEventListener('mousemove', (event) => {
      if (!dragging) return;
      event.preventDefault();
      const moved = 2 * (event.pageX - track.offsetLeft - startX);
      track.scrollLeft = startScrollLeft - moved;
    });
  }
}

customElements.define('featured-products', FeaturedProducts);
